// SPDX-FileCopyrightText: 2025 Slant Tech LLC
// SPDX-License-Identifier: GPL-3.0-or-later

import { useState, useMemo } from 'react';
import CommentCard from './CommentCard';
import NewCommentForm from './NewCommentForm';

function CommentPanel({
  comments,
  selectedComment,
  isAddingComment,
  newCommentPosition,
  gitlabUrl = '',
  projectId,
  onStartAdd,
  onCancelAdd,
  onCreateComment,
  onSelectComment,
  onUpdateComment,
  onDeleteComment,
  onResolveComment,
}) {
  const [filter, setFilter] = useState('all'); // 'all', 'open', 'resolved'
  const [editingId, setEditingId] = useState(null);

  // Filter comments
  const filteredComments = useMemo(() => {
    if (filter === 'all') return comments;
    if (filter === 'open') return comments.filter(c => !c.resolved);
    if (filter === 'resolved') return comments.filter(c => c.resolved);
    return comments;
  }, [comments, filter]);

  // Stats
  const openCount = comments.filter(c => !c.resolved).length;
  const resolvedCount = comments.filter(c => c.resolved).length;

  const handleStartEdit = (commentId) => {
    setEditingId(commentId);
  };

  const handleCancelEdit = () => {
    setEditingId(null);
  };

  const handleSaveEdit = async (commentId, body) => {
    await onUpdateComment(commentId, { content: body });
    setEditingId(null);
  };

  return (
    <div className="w-80 flex-shrink-0 border-l-2 border-st-border bg-st-primary flex flex-col">
      {/* Header */}
      <div className="p-4 border-b-2 border-st-border">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display font-semibold text-lg flex items-center gap-2">
            <svg className="w-5 h-5 text-st-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
            Comments
          </h2>
          <button
            onClick={isAddingComment ? onCancelAdd : onStartAdd}
            className={`btn ${isAddingComment ? 'btn-secondary' : 'btn-primary'}`}
            disabled={!!newCommentPosition}
          >
            {isAddingComment ? (
              <>
                <span>Cancel</span>
              </>
            ) : (
              <>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Add
              </>
            )}
          </button>
        </div>

        {/* Stats */}
        <div className="flex gap-3 text-sm font-mono">
          <span className="text-st-accent">{openCount} open</span>
          <span className="text-st-success">{resolvedCount} resolved</span>
        </div>

        {/* Filter */}
        <div className="flex gap-1 mt-3">
          {['all', 'open', 'resolved'].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-3 py-1.5 text-xs font-display font-semibold uppercase tracking-wider transition-colors ${
                filter === f
                  ? 'bg-st-accent text-white'
                  : 'text-st-muted hover:text-st-accent border-2 border-transparent hover:border-st-border'
              }`}
            >
              {f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* New Comment Form */}
      {newCommentPosition && (
        <div className="p-4 border-b-2 border-st-border bg-st-secondary animate-slide-in">
          <NewCommentForm
            position={newCommentPosition}
            onSubmit={onCreateComment}
            onCancel={onCancelAdd}
          />
        </div>
      )}

      {/* Comments List */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {filteredComments.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon"><i className="bx bx-comment"></i></div>
            <p className="text-sm font-body">
              {filter === 'all'
                ? 'No comments yet'
                : filter === 'open'
                ? 'No open comments'
                : 'No resolved comments'}
            </p>
            {filter === 'all' && !isAddingComment && (
              <button
                onClick={onStartAdd}
                className="btn btn-primary mt-4"
              >
                Add First Comment
              </button>
            )}
          </div>
        ) : (
          filteredComments.map((comment, index) => (
            <CommentCard
              key={comment.id}
              comment={comment}
              index={comments.indexOf(comment) + 1}
              gitlabUrl={gitlabUrl}
              projectId={projectId}
              isSelected={selectedComment?.id === comment.id}
              isEditing={editingId === comment.id}
              onClick={() => onSelectComment(comment)}
              onEdit={() => handleStartEdit(comment.id)}
              onCancelEdit={handleCancelEdit}
              onSaveEdit={(body) => handleSaveEdit(comment.id, body)}
              onResolve={(resolved) => onResolveComment(comment.id, resolved)}
              onDelete={() => onDeleteComment(comment.id)}
            />
          ))
        )}
      </div>
    </div>
  );
}

export default CommentPanel;
